/**
 * What a sale really costs, shown inline under the event that causes it.
 * Every figure comes from running the plan with and without the sale — no
 * rules of thumb, and no pretending 15% is the whole story.
 */
import { useMemo } from 'react';
import type { Plan } from '../model/types';
import { analyzeSale } from '../engine/saleImpact';
import { fmtUsd, fmtPct } from './format';

export function SaleImpactNote({ plan, eventId }: { plan: Plan; eventId: string }) {
  const impact = useMemo(() => analyzeSale(plan, eventId), [plan, eventId]);
  if (!impact || impact.gain <= 0) return null;

  const rows: { label: string; value: number }[] = [
    { label: 'Federal capital-gains tax', value: impact.capitalGainsTax },
    { label: 'Net investment income surtax (3.8%)', value: impact.niit },
    { label: 'Extra ordinary-rate tax', value: impact.otherFederalTax },
    { label: 'State tax', value: impact.stateTax },
    { label: 'ACA subsidy lost that year', value: impact.acaSubsidyLost },
    { label: 'Higher Medicare premiums (next 2 years)', value: impact.irmaaSurcharge },
  ].filter((r) => r.value >= 1);

  return (
    <div className="sale-impact">
      <strong>What this sale costs in {impact.year}</strong>
      <p className="hint" style={{ margin: '2px 0 8px' }}>
        {fmtUsd(impact.proceeds)} in, minus {fmtUsd(impact.costBasis)} of basis, is a{' '}
        {fmtUsd(impact.gain)} gain. Measured by running your plan with and without it.
      </p>
      <table className="sale-impact-table">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td>{fmtUsd(r.value)}</td>
            </tr>
          ))}
          <tr className="sale-impact-total">
            <td>All-in cost</td>
            <td>{fmtUsd(impact.totalCost)}</td>
          </tr>
          <tr>
            <td>Share of the proceeds</td>
            <td>{fmtPct(impact.effectiveRateOnProceeds)}</td>
          </tr>
        </tbody>
      </table>
      {impact.crossedIntoTopRate && (
        <p className="hint">
          Part of this gain lands in the 20% capital-gains bracket. Splitting the sale across
          two tax years — or an installment sale — would keep more of it at 15%.
        </p>
      )}
      {impact.acaSubsidyLost > 0 && (
        <p className="hint">
          This wipes out your ACA subsidy for {impact.year}. Selling after 65, or in a year you
          are not buying marketplace coverage, avoids that.
        </p>
      )}
    </div>
  );
}
